import { createModel } from '@rematch/core';
import { RootModel } from './RootState';
import type { FeatureOptions } from '../api/models/FeatureOptions';
import type { SidebarFeatureItem } from '../models/features/sidebar/SidebarFeatureItem';
import type { MainFeatureItem } from '../models/features/main/MainFeatureItem';
import { MenuFeatureItem } from '../models/features/menu/MenuFeatureItem';
import {
    applyFilters,
    backToPreviousFeatureItems,
    restoreFeatureItem,
    startFeatureItem
} from '../services/FeatureService';
import { AppliedFilters } from '../models/AppliedFilters';
import { Product } from '../models/Product';
import { productSearchByImageMenuFeatureHandler } from '../services/features/menu/ProductSearchByImageMenuFeatureHandler';
import { productMultiVariantAddToCartMainFeatureHandler } from '../services/features/main/ProductMultiVariantAddToCartMainFeatureHandler';
import { productComparisonMainFeatureHandler } from '../services/features/main/ProductComparisonMainFeatureHandler';
import { chatSidebarFeatureHandler } from '../services/features/sidebar/ChatSidebarFeatureHandler';
import type { ChatMessage } from '../models/ChatMessage';
import type { Except } from 'type-fest';
import type { RootState } from './store';
import type { Feature } from '@public-api-contracts/models/chat/ChatMessage';

export type FeatureState = {
    mainFeatureItem: null | MainFeatureItem;
    sidebarExpanded: boolean;
    sidebarFeatureItem: null | SidebarFeatureItem;
    isFilterPanelOpen: boolean;
    isBoundingBoxSelectorPanelOpen: boolean;
    menuFeatureItem: null | MenuFeatureItem;
};

export const initialState: FeatureState = {
    mainFeatureItem: null,
    sidebarExpanded: false,
    sidebarFeatureItem: null,
    isFilterPanelOpen: false,
    isBoundingBoxSelectorPanelOpen: false,
    menuFeatureItem: null
};

export const featureState = createModel<RootModel>()({
    state: initialState,
    reducers: {
        reset() {
            return initialState;
        },
        setMainFeatureItem(state, mainFeatureItem: MainFeatureItem | null) {
            return { ...state, mainFeatureItem };
        },
        updateMainFeatureItem(state, mainFeatureItem: MainFeatureItem) {
            if (
                state.mainFeatureItem?.featureUuid !==
                mainFeatureItem.featureUuid
            ) {
                return state;
            }
            return { ...state, mainFeatureItem };
        },
        setSidebarExpanded(state, sidebarExpanded: boolean) {
            return { ...state, sidebarExpanded };
        },
        setSidebarFeatureItem(
            state,
            sidebarFeatureItem: SidebarFeatureItem | null
        ) {
            return { ...state, sidebarFeatureItem };
        },
        updateSidebarFeatureItem(
            state,
            sidebarFeatureItem: SidebarFeatureItem
        ) {
            if (
                state.sidebarFeatureItem?.featureUuid !==
                sidebarFeatureItem.featureUuid
            ) {
                return state;
            }
            return { ...state, sidebarFeatureItem };
        },
        setFilterPanelOpen(state, isFilterPanelOpen: boolean) {
            return { ...state, isFilterPanelOpen };
        },
        setMenuFeatureItem(state, menuFeatureItem: MenuFeatureItem | null) {
            return { ...state, menuFeatureItem };
        },
        updateMenuFeatureItem(state, menuFeatureItem: MenuFeatureItem) {
            if (
                state.menuFeatureItem?.featureUuid !==
                menuFeatureItem?.featureUuid
            ) {
                return state;
            }
            return { ...state, menuFeatureItem };
        },
        removeProductFromComparison(state, productId: string) {
            return productComparisonMainFeatureHandler.removeProduct(
                state,
                productId
            );
        },
        setExpiredAt(state, expiredAt: string) {
            return { ...state, expiredAt };
        },
        removeProductFromMultiVariantAddToCart(state, productId: string) {
            return productMultiVariantAddToCartMainFeatureHandler.removeProduct(
                state,
                productId
            );
        }
    },
    effects: dispatch => ({
        async startFeature(
            featureOptions: FeatureOptions,
            state
        ): Promise<void> {
            await startFeatureItem(dispatch, state, featureOptions);
        },
        restoreFeatureItem(feature: Feature, state): void {
            restoreFeatureItem(dispatch, state, feature);
        },
        backToPreviousFeatureItems(_: void, state) {
            backToPreviousFeatureItems(dispatch, state);
        },
        updateContext(_: void, state) {
            return chatSidebarFeatureHandler.updateChatContext(state);
        },
        appendNewChatMessage(
            chatMessage: Except<ChatMessage, 'timestamp' | 'uuid'>,
            state: RootState
        ): ChatMessage | null {
            return chatSidebarFeatureHandler.appendNewChatMessage(
                dispatch,
                state,
                chatMessage
            );
        },
        patchExistingChatMessage(
            data: Partial<ChatMessage> & { uuid: string },
            state: RootState
        ): { index: number; chatMessage: ChatMessage | null } {
            return chatSidebarFeatureHandler.patchExistingChatMessage(
                dispatch,
                state,
                data
            );
        },
        appendAddToCartErrorMessage(_: void, state) {
            return chatSidebarFeatureHandler.appendAddToCartErrorMessage(
                dispatch,
                state
            );
        },
        appendProductAddedToCartMessage(
            data: { product: Product; variantId: string },
            state
        ) {
            return chatSidebarFeatureHandler.appendProductAddedToCartMessage(
                dispatch,
                state,
                data
            );
        },
        appendProductSearchByImageMessage(_: void, state) {
            return chatSidebarFeatureHandler.appendProductSearchByImageMessage(
                dispatch,
                state
            );
        },
        removeProductSearchByImageMessage(_: void, state: RootState) {
            return chatSidebarFeatureHandler.removeSearchByImageMessages(
                dispatch,
                state
            );
        },
        sendMessage(options: { handleLoading: boolean }, state) {
            return chatSidebarFeatureHandler.sendMessage(
                dispatch,
                state,
                options
            );
        },
        uploadImage(options: { imageDataUrl: string }, state) {
            return productSearchByImageMenuFeatureHandler.uploadImage(
                dispatch,
                state,
                options
            );
        },
        changeSelectedBoundingBox(_: void, state) {
            dispatch.featureState.setSidebarExpanded(true);
            dispatch.featureState.removeProductSearchByImageMessage();
            dispatch.featureState.appendProductSearchByImageMessage();
        },
        async applyProductSearchFilters(
            appliedFilters: Partial<AppliedFilters>,
            state
        ) {
            await applyFilters(dispatch, state, appliedFilters);
            chatSidebarFeatureHandler.appendNewChatMessage(dispatch, state, {
                role: 'assistant',
                content: {
                    contentType: 'apply-filters',
                    fieldFilters: appliedFilters.appliedFieldFilters || [],
                    categoryFilters: appliedFilters.appliedCategoryFilters || []
                }
            });
        },
        async loadSuggestedProductsAfterMultiVariantAddToCartSuccess(
            products: Product[],
            state
        ) {
            await productMultiVariantAddToCartMainFeatureHandler.loadSuggestedProducts(
                dispatch,
                state,
                products
            );
        }
    })
});
